import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import * as Sentry from '@sentry/react'

import { RootState } from '../../../appReducer'
import {
    fetchLiveWebChatSuggestionDispositions,
    recordLiveWebChatSuggestionCardDisplayed,
    recordLiveWebChatSuggestionDisposition,
} from '../actions'

import { SendResult } from './send-result'

import './AiSuggestionCard.sass'

export type Props = {
    channelName: string
    onEdit: (text: string, effectId: string) => void
    onSend: (text: string) => Promise<SendResult>
    // `025-` M2: the sibling manual input is disabled while the chat is banned or ended
    // (`ChatTextField.tsx`'s own `isBanned || isChatEnded()`); this card rendered its one-click
    // Send/Edit unconditionally outside that gate, so a specialist could still fire a suggestion
    // into a closed or banned conversation.
    disabled: boolean
}

const moduleName = 'ai-suggestion-card'

// Fixed, explicit disclosure label - deliberately NOT `suggestion.sourceAttribution`.
// `sourceAttribution` is machine provenance on the wire (currently the literal
// "live_web_chat_ai_agent"; see `LiveWebChatAiSuggestionEffectV1`), not display text - echoing it
// verbatim would show the specialist a raw snake_case slug instead of a real disclosure that the
// suggestion is AI-generated.
const AI_SUGGESTION_LABEL = 'AI-generated suggestion'

// The suggestion is delivered async over PubNub, not requested by this component - there is no
// call to await, so "how long has this been waiting" can't come from a request lifecycle. The
// real model call has no timeout configured upstream and routinely runs past the relay's own 5s
// ingress timeout, so this needs to be generous rather than tuned to a "normal" latency.
const SUGGESTION_WAIT_TIMEOUT_MS = 45000

const AiSuggestionCard = (props: Props) => {
    const { channelName, onEdit, onSend, disabled } = props
    const dispatch = useDispatch()

    // Tracks the newest patient turn we've seen arrive live, and whether it has timed out waiting
    // for a suggestion. Not derived from timetoken vs Date.now() - timetoken is a PubNub/server
    // clock and this repo has already been bitten by treating it as comparable to wall-clock time
    // (a WSL-drift-stamped chat silently vanished from the list). Instead this is seeded from
    // whatever's already on screen on first render, so pre-existing unanswered messages never
    // trigger a spinner - only a turn that arrives while this component is mounted does.
    const [pendingTurn, setPendingTurn] = useState<{ id: string; timedOut: boolean } | null>(null)
    const seenLatestPatientMessageId = useRef<string | null>(null)
    const hasSeededForChannel = useRef(false)

    // Optimistic local hide, component-scoped (not the old module-level Set): Edit/Send add to
    // this synchronously so the card disappears on the same click instead of waiting on the Redux
    // round trip. `suggestionDispositions` (fetched per channel below) is the durable,
    // reload-surviving check; this is only a same-mount UI/dedup optimization on top of it.
    const [locallyDispositionedEffectIds, setLocallyDispositionedEffectIds] = useState<Set<string>>(new Set())

    const markLocallyDispositioned = (effectId: string) => {
        setLocallyDispositionedEffectIds(previous => new Set(previous).add(effectId))
    }

    const unmarkLocallyDispositioned = (effectId: string) => {
        setLocallyDispositionedEffectIds(previous => {
            const next = new Set(previous)
            next.delete(effectId)
            return next
        })
    }

    const dispositionsLoadedRef = useRef(false)
    const visibleEffectIdRef = useRef<string | null>(null)
    const ackedDisplayedEffectIds = useRef<Set<string>>(new Set())
    // Fire-and-forget display evidence, once per effectId per mount, only after the stored
    // dispositions for this channel have been read. Never awaited; never gates render, Send, or Edit.
    const ackDisplayedIfDue = useCallback(() => {
        const effectId = visibleEffectIdRef.current
        if (!dispositionsLoadedRef.current || !effectId || ackedDisplayedEffectIds.current.has(effectId)) {
            return
        }
        ackedDisplayedEffectIds.current.add(effectId)
        dispatch(recordLiveWebChatSuggestionCardDisplayed(effectId)).catch((err: unknown) => {
            Sentry.captureException(err, {
                tags: { feature: 'ai-suggestion-card-displayed' },
                extra: { effectId },
            })
        })
    }, [dispatch])

    useEffect(() => {
        setPendingTurn(null)
        seenLatestPatientMessageId.current = null
        hasSeededForChannel.current = false
        // Closes OQ-T's reload gap: dispositions are fetched fresh per channel so an already
        // accepted/edited/rejected suggestion never comes back with live Edit/Send buttons after a
        // reload or a switch back to this chat. `025-` H5: a failed fetch used to fail silently,
        // reopening that same gap on a transient network error - now at least surfaced for
        // diagnosis, since there's no retry UI to fall back to here.
        dispositionsLoadedRef.current = false
        dispatch(fetchLiveWebChatSuggestionDispositions(channelName))
            .then(() => {
                // The card-displayed ack waits for this. On first mount `isVisible` is computed before
                // this GET has populated `suggestionDispositions`, so without the gate a post-reload
                // re-mount would stamp a suggestion that was already rejected on tile close. A failed
                // GET leaves the stamp unwritten, which the acceptance report reads as "unknown" — the
                // honest answer. A ref (not state) so the gate flipping causes no re-render.
                dispositionsLoadedRef.current = true
                ackDisplayedIfDue()
            })
            .catch((err: unknown) => {
                Sentry.captureException(err, {
                    tags: { feature: 'ai-suggestion-dispositions' },
                    extra: { channelName },
                })
            })
    }, [channelName, dispatch, ackDisplayedIfDue])

    const suggestionDispositions = useSelector((state: RootState) => state.chat.suggestionDispositions)

    const whisperMessages = useSelector(
        (state: RootState) => state.chat.conversations[`whisper_${channelName}`]?.messages,
    )
    const conversationMessages = useSelector((state: RootState) => state.chat.conversations[channelName]?.messages)

    const latestPatientMessage = (conversationMessages || [])
        .filter(message => message.is_patient && message.type === 'text')
        .sort((a, b) => Number(b.timetoken) - Number(a.timetoken))[0]

    useEffect(() => {
        // `025-` M1: seeding on first render (rather than on first *loaded* render) raced history
        // load. `conversationMessages` is `undefined` until `RECEIVE_CHAT_HISTORY`/
        // `RECEIVE_HISTORY_BATCH` populate this channel's entry, so the seed effect used to latch
        // onto that undefined-derived `null` before any history arrived, then treat history's first
        // real patient message as a brand-new turn - a false 45s spinner, then a false failure, for
        // a message that may be hours old and already answered. Wait for the raw selector (not the
        // `|| []`-defaulted `latestPatientMessage`) to actually resolve before latching the seed.
        if (conversationMessages === undefined) {
            return
        }

        if (!hasSeededForChannel.current) {
            hasSeededForChannel.current = true
            seenLatestPatientMessageId.current = latestPatientMessage?.id ?? null
            return
        }

        if (latestPatientMessage && latestPatientMessage.id !== seenLatestPatientMessageId.current) {
            seenLatestPatientMessageId.current = latestPatientMessage.id
            setPendingTurn({ id: latestPatientMessage.id, timedOut: false })
        }
    }, [latestPatientMessage, conversationMessages])

    useEffect(() => {
        if (!pendingTurn || pendingTurn.timedOut) {
            return
        }

        const turnId = pendingTurn.id
        const timer = setTimeout(() => {
            setPendingTurn(current => (current && current.id === turnId ? { ...current, timedOut: true } : current))
        }, SUGGESTION_WAIT_TIMEOUT_MS)

        return () => clearTimeout(timer)
    }, [pendingTurn])

    const suggestion = (whisperMessages || [])
        .filter(message => message.type === 'ai_suggestion' && message.effectId)
        .sort((a, b) => Number(b.timetoken) - Number(a.timetoken))[0]

    const hasUsableSuggestion = Boolean(
        suggestion &&
            suggestion.effectId &&
            !suggestionDispositions[suggestion.effectId] &&
            !locallyDispositionedEffectIds.has(suggestion.effectId) &&
            (!latestPatientMessage || Number(suggestion.timetoken) >= Number(latestPatientMessage.timetoken)),
    )

    // Also stale, not just "used": if the specialist has already replied by hand - not through
    // Edit or Send - since this suggestion appeared, it's answering a question that's already been
    // handled. Leaving it up risks a later, out-of-context click. `is_patient: false` is the same
    // flag ChatConversation.tsx already uses to distinguish specialist from patient messages.
    //
    // Also requires `type === 'text'`: PubNubEventManager and friends publish synthetic
    // `is_patient: false` system messages (`system:chatter-rejoined-chat`, `system:closed`, ban/
    // payment-form events, etc.) onto this same channel for presence churn and other non-reply
    // events, each with a fresh timetoken. Without the type check, an ordinary reconnect right
    // after a suggestion was delivered reads as "the specialist already replied," and this effect
    // auto-rejects the suggestion before anyone ever sees it. `useMessaging.tsx` and
    // `latestPatientMessage` above already gate on this same `type === 'text'` convention for real
    // human-authored messages.
    //
    // Anchor on the patient turn being answered, not the suggestion's own timetoken - `025-` H4.
    // Anchoring on the suggestion let a slow-arriving suggestion swap the anchor past a specialist
    // reply that landed between the patient's turn and the suggestion showing up (patient T1 ->
    // specialist replies by hand T2 -> suggestion for T1 arrives late at T3 > T2), so
    // `specialistHasRepliedSince` looked for a reply after T3 and missed the real one at T2. The
    // patient-turn anchor is always at least as old as a usable suggestion's own timetoken
    // (`hasUsableSuggestion` already requires `suggestion.timetoken >= latestPatientMessage.timetoken`),
    // so this doesn't regress the mid-wait case (no suggestion at all, `hasUsableSuggestion` false)
    // where the patient turn was already the anchor. Falls back to the suggestion's timetoken only
    // when there's no patient message to anchor on at all.
    const repliedSinceTimetoken = latestPatientMessage?.timetoken ?? suggestion?.timetoken

    const specialistHasRepliedSince =
        repliedSinceTimetoken !== undefined &&
        (conversationMessages || []).some(
            message =>
                !message.is_patient &&
                message.type === 'text' &&
                Number(message.timetoken) > Number(repliedSinceTimetoken),
        )

    const isAwaitingSuggestion =
        pendingTurn !== null &&
        latestPatientMessage?.id === pendingTurn.id &&
        !hasUsableSuggestion &&
        !specialistHasRepliedSince

    const isVisible = Boolean(suggestion && suggestion.effectId && hasUsableSuggestion && !specialistHasRepliedSince)

    // Display evidence, not proof the specialist read it; a backgrounded tab is the honest residual.
    // ChatTile.tsx already renders ChatTextField (and so this card) only inside its on-screen
    // isVisible window. The ref is per mount: a scroll-away unmount and scroll-back re-posts,
    // which is why the api is first-write-wins.
    // Two triggers reach the same ack: the visibility effect below (when the card becomes visible
    // after the dispositions are loaded) and the fetch continuation above (when the dispositions
    // finish loading while the card is already visible). Both read the current visible effectId
    // from a ref, so neither needs a re-render.
    useEffect(() => {
        visibleEffectIdRef.current = isVisible && suggestion?.effectId ? suggestion.effectId : null
        ackDisplayedIfDue()
    }, [isVisible, suggestion?.effectId, ackDisplayedIfDue])

    if (isAwaitingSuggestion) {
        return (
            <div className={moduleName}>
                <div className={`${moduleName}__wrapper`}>
                    <div className={`${moduleName}__content`}>
                        <div className={`${moduleName}__attribution`}>{AI_SUGGESTION_LABEL}</div>
                        {pendingTurn?.timedOut ? (
                            <div className={`${moduleName}__status ${moduleName}__status--failed`}>
                                Couldn&rsquo;t get a suggestion for this message.
                            </div>
                        ) : (
                            <div className={`${moduleName}__status`}>
                                <CircularProgress thickness={5} size={17} color="primary" variant="indeterminate" />
                                <span>Drafting a reply&hellip;</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (!isVisible || !suggestion || !suggestion.effectId) {
        return null
    }

    const { text, effectId } = suggestion

    // A failed telemetry write never reverts the hide: the specialist already saw Send/Edit
    // succeed, and undoing that would take back an action they already took. A refused send is
    // a different fact — handleSend un-hides that case after the send result arrives.
    // disposeStaleAiSuggestionOnChatClose still covers resurrection if the tile closes first.
    const recordDisposition = (disposition: ApiV2.Amplify.LiveWebChatSuggestionDisposition) => {
        dispatch(recordLiveWebChatSuggestionDisposition(effectId, disposition)).catch((err: unknown) => {
            Sentry.captureException(err, {
                tags: { feature: 'ai-suggestion-dispositions' },
                extra: { effectId, disposition },
            })
        })
    }

    const handleEdit = () => {
        markLocallyDispositioned(effectId)
        setPendingTurn(null)
        onEdit(text, effectId)
        recordDisposition('edited')
    }

    const handleSend = async () => {
        markLocallyDispositioned(effectId)
        setPendingTurn(null)
        let result: SendResult = { kind: 'unknown' }
        try {
            result = (await onSend(text)) ?? { kind: 'unknown' }
        } catch {
            // A rejected send is unknown, not an unhandled rejection.
        }
        if (result.kind === 'admitted' || result.kind === 'published') {
            recordDisposition('accepted')
        } else if (result.kind === 'refused') {
            unmarkLocallyDispositioned(effectId)
        }
    }

    return (
        <div className={moduleName} data-testid="ai-suggestion">
            <div className={`${moduleName}__wrapper`}>
                <div className={`${moduleName}__content`}>
                    <div className={`${moduleName}__attribution`}>{AI_SUGGESTION_LABEL}</div>
                    <div className={`${moduleName}__text`} data-testid="ai-suggestion-text">
                        {text}
                    </div>
                    <div className={`${moduleName}__actions`}>
                        <Button
                            className={`${moduleName}__edit-button`}
                            data-testid="ai-suggestion-edit"
                            onClick={handleEdit}
                            disabled={disabled}
                        >
                            Edit
                        </Button>
                        <Button
                            className={`${moduleName}__send-button`}
                            data-testid="ai-suggestion-send"
                            variant="contained"
                            onClick={handleSend}
                            disabled={disabled}
                        >
                            Send
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AiSuggestionCard
